// Sign-in, ported from the prototype with its demo accounts; plus the authenticator-code step,
// "forgot password" and setting a password from a reset or invitation link (phase 9).
import { ApiError, LOCALES, locale, login, post, setLocale, t, useOnLogin } from "@keel/core";
import { type FormEvent, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const DEMO = [
  ["owner@keel.dev", "Owner"],
  ["sales@keel.dev", "Sales"],
  ["warehouse@keel.dev", "Warehouse"],
  ["finance@keel.dev", "Finance"],
  ["production@keel.dev", "Production"],
  ["owner@vidzeme.dev", "Other customer (Core plan)"],
];

export function Login() {
  const loc = useLocation();
  if (loc.pathname === "/reset-password")
    return <SetPassword token={new URLSearchParams(loc.search).get("token") ?? ""} />;
  return <SignIn />;
}

function SignIn() {
  const onLogin = useOnLogin();
  const [email, setEmail] = useState("owner@keel.dev");
  const [password, setPassword] = useState("keel-demo");
  const [code, setCode] = useState("");
  const [needCode, setNeedCode] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [sso, setSso] = useState(false);
  const [account, setAccount] = useState("");
  const ssoError = new URLSearchParams(location.search).get("sso_error");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e?: FormEvent, as?: string) => {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (sso) {
        location.href = `/api/v1/auth/oidc/${encodeURIComponent(account.trim().toLowerCase())}/start`;
        return;
      }
      if (forgot) {
        await post("/auth/password-reset", { email });
        setSent(true);
        return;
      }
      await login(as ?? email, password, needCode ? code : undefined);
      onLogin();
    } catch (err) {
      if (err instanceof ApiError && err.code === "mfa_required") {
        setNeedCode(true);
        return;
      }
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login">
      <div className="login-pane">
        <div className="row" style={{ gap: 12 }}>
          <svg width="34" height="34" viewBox="0 0 32 32" aria-hidden="true">
            <rect width="32" height="32" rx="8" className="brand-bg" />
            <path d="M8 11h16l-3 9h-10z" className="brand-hull" />
            <path d="M16 20v5" stroke="var(--accent)" strokeWidth="3" strokeLinecap="round" />
          </svg>
          <b style={{ fontSize: 18, letterSpacing: "-0.02em" }}>Keel</b>
        </div>
        <div>
          <h1>{t("Sign in")}</h1>
          <p className="sub">{t("Sales, stock, finance and every system they talk to.")}</p>
        </div>
        {ssoError && (
          <div className="banner banner-danger">
            {ssoError === "not_allowed" || ssoError === "not_a_member"
              ? "Your company's sign-in worked, but this account has no access for you yet. Ask an administrator."
              : ssoError === "no_provider"
                ? "That account has no single sign-on set up."
                : "Single sign-on didn't complete. Try again."}
          </div>
        )}
        {sso ? (
          <form onSubmit={submit}>
            <label className="field">
              <span>{t("Account")}</span>
              <input
                value={account}
                onChange={(e) => setAccount(e.target.value)}
                placeholder="baltic-workshop"
                required
              />
            </label>
            <button type="submit" className="btn btn-primary" style={{ height: 40 }}>
              {t("Continue to your identity provider")}
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSso(false)}>
              {t("Back to sign in")}
            </button>
          </form>
        ) : (
          <form onSubmit={submit}>
            <label className="field">
              <span>{t("Email")}</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
              />
            </label>
            {!forgot && (
              <label className="field">
                <span>{t("Password")}</span>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </label>
            )}
            {needCode && !forgot && (
              <label className="field">
                <span>{t("Authenticator code")}</span>
                <input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="123 456"
                  // biome-ignore lint/a11y/noAutofocus: the next thing to type
                  autoFocus
                  required
                />
              </label>
            )}
            {error && <div className="banner banner-danger">{error}</div>}
            {sent && (
              <div className="banner">
                {t("If that address has an account, a link to choose a new password is on its way.")}
              </div>
            )}
            <button
              type="submit"
              className="btn btn-primary"
              style={{ height: 40 }}
              disabled={busy || (forgot && sent)}
            >
              {forgot ? t("Send reset link") : busy ? t("Signing in…") : t("Sign in")}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => {
                setForgot(!forgot);
                setSent(false);
                setError(null);
              }}
            >
              {forgot ? t("Back to sign in") : t("Forgot password?")}
            </button>
            <button type="button" className="btn btn-sm" onClick={() => setSso(true)}>
              {t("Sign in with SSO")}
            </button>
          </form>
        )}
        <div className="row" style={{ gap: 6 }}>
          {LOCALES.map((l) => (
            <button
              key={l.value}
              type="button"
              className={`chip ${l.value === locale ? "active" : ""}`}
              onClick={() => setLocale(l.value)}
            >
              {l.label}
            </button>
          ))}
        </div>
        <div className="stack" style={{ gap: 8 }}>
          <span className="hint">
            Demo accounts (password <span className="inline-code">keel-demo</span>) — each role sees a
            different app:
          </span>
          <div className="demo-logins">
            {DEMO.map(([e, r]) => (
              <button
                key={e}
                className="btn btn-sm"
                type="button"
                onClick={() => {
                  setEmail(e as string);
                  submit(undefined, e);
                }}
              >
                {r}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="login-art">
        <div className="row">
          <span
            className="sandbox-tag"
            style={{
              background: "transparent",
              color: "inherit",
              borderColor: "color-mix(in oklab, var(--bg) 25%, transparent)",
            }}
          >
            API-first ERP
          </span>
        </div>
        <div className="stack" style={{ gap: 18 }}>
          <h2>The ERP that treats integrations as part of the product.</h2>
          <p>
            Every order, stock movement and invoice is an event. Webhooks, connectors and a health view come
            built in — not bolted on.
          </p>
          <div className="flow">
            <div>
              <i />
              Shopify<span>inventory.changed · 38ms</span>
            </div>
            <div>
              <i />
              PIM — Syncryo<span>product.updated · 64ms</span>
            </div>
            <div>
              <i className="warn" />
              Warehouse — 3PL<span>3 failed messages</span>
            </div>
            <div>
              <i className="bad" />
              Marketplace<span>authentication failure</span>
            </div>
          </div>
        </div>
        <small style={{ opacity: 0.5 }}>REST · Webhooks · OpenAPI · Multi-tenant</small>
      </div>
    </div>
  );
}

/** Choose a password from a reset or invitation link. */
function SetPassword({ token }: { token: string }) {
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password !== repeat) {
      setError(t("The passwords don't match"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await post("/auth/password-reset/confirm", { token, password });
      setDone(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="login">
      <div className="login-pane">
        <div>
          <h1>{t("Choose a password")}</h1>
          <p className="sub">At least 10 characters; not a common password and not your e-mail name.</p>
        </div>
        {done ? (
          <>
            <div className="banner">{t("Your password is set. Sign in with it now.")}</div>
            <button type="button" className="btn btn-primary" onClick={() => nav("/", { replace: true })}>
              {t("Go to sign in")}
            </button>
          </>
        ) : (
          <form onSubmit={submit}>
            <label className="field">
              <span>{t("New password")}</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </label>
            <label className="field">
              <span>{t("Repeat it")}</span>
              <input
                type="password"
                value={repeat}
                onChange={(e) => setRepeat(e.target.value)}
                autoComplete="new-password"
                required
              />
            </label>
            {error && <div className="banner banner-danger">{error}</div>}
            <button
              type="submit"
              className="btn btn-primary"
              style={{ height: 40 }}
              disabled={busy || !token}
            >
              {t("Set password")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
